import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Typography,
} from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import InventoryIcon from "@mui/icons-material/Inventory";
import { DeliveryStats } from "../../../types/analytics";

interface DeliveryStatsCardProps {
  title: string;
  data: DeliveryStats;
}

const DeliveryStatsCard: React.FC<DeliveryStatsCardProps> = ({
  title,
  data,
}) => {
  const total = data.totalItemsOrdered || 1;
  const deliveredPct = Math.round((data.itemsDelivered / total) * 100);
  const shippedPct = Math.round((data.itemsShipped / total) * 100);
  const pendingPct = Math.round((data.itemsPending / total) * 100);

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {title}
        </Typography>

        {/* Pipeline stats */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 100, textAlign: "center" }}>
            <InventoryIcon sx={{ fontSize: 28, color: "info.main", mb: 0.5 }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {data.totalItemsOrdered}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Items
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 100, textAlign: "center" }}>
            <HourglassEmptyIcon
              sx={{ fontSize: 28, color: "warning.main", mb: 0.5 }}
            />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {data.itemsPending}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Pending
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 100, textAlign: "center" }}>
            <LocalShippingIcon
              sx={{ fontSize: 28, color: "primary.main", mb: 0.5 }}
            />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {data.itemsShipped}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Shipped
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 100, textAlign: "center" }}>
            <CheckCircleIcon
              sx={{ fontSize: 28, color: "success.main", mb: 0.5 }}
            />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {data.itemsDelivered}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Delivered
            </Typography>
          </Box>
        </Box>

        {/* Progress bar */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", gap: 0 }}>
            <LinearProgress
              variant="determinate"
              value={100}
              sx={{
                flex: deliveredPct || 1,
                height: 12,
                borderRadius: "6px 0 0 6px",
                backgroundColor: "transparent",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "success.main",
                },
              }}
            />
            <LinearProgress
              variant="determinate"
              value={100}
              sx={{
                flex: shippedPct || 1,
                height: 12,
                backgroundColor: "transparent",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "primary.main",
                },
              }}
            />
            <LinearProgress
              variant="determinate"
              value={100}
              sx={{
                flex: pendingPct || 1,
                height: 12,
                borderRadius: "0 6px 6px 0",
                backgroundColor: "transparent",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "warning.main",
                },
              }}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
            <Typography variant="caption" color="success.main">
              {deliveredPct}% delivered
            </Typography>
            <Typography variant="caption" color="primary.main">
              {shippedPct}% shipped
            </Typography>
            <Typography variant="caption" color="warning.main">
              {pendingPct}% pending
            </Typography>
          </Box>
        </Box>

        {/* Avg delivery days */}
        <Chip
          label={`Avg. delivery: ${data.averageDeliveryDays} days`}
          size="small"
          color="info"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      </CardContent>
    </Card>
  );
};

export default DeliveryStatsCard;
